import { Col, Form, FormInstance, Input, message, Row, Select } from 'antd'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import { useFetchAddressFromPostCode } from 'api/postcodes'
import { Button } from 'components/shared/Button'
import { Spinner } from 'components/shared/Spinner'
import { useCountries } from 'hooks/static'
import { useTranslation } from 'next-i18next'
import { FC, useMemo } from 'react'

export const AddressForm: FC<{ form: FormInstance }> = ({ form }) => {
  const { xs } = useBreakpoint()
  const { t } = useTranslation()

  const { data: countries } = useCountries()
  const { fetchAddressFromPostCode } = useFetchAddressFromPostCode()
  const japan = useMemo(
    () => countries?.find((country) => country.translations.en === 'Japan'),
    [countries],
  )

  const handleAddressFromPostalInit = async () => {
    const code = form.getFieldValue([
      'addresses_attributes',
      '0',
      'postal_code',
    ])
    if (!code) {
      return
    }

    const res = await fetchAddressFromPostCode(code)
    if (res.status !== 200 || !res.results) {
      message.error(t('郵便番号から住所を取得できませんでした'))
      return
    }

    form.setFieldValue(
      ['addresses_attributes', '0', 'state'],
      res.results[0].address1,
    )
    form.setFieldValue(
      ['addresses_attributes', '0', 'city'],
      res.results[0].address2,
    )
    form.setFieldValue(
      ['addresses_attributes', '0', 'address1'],
      res.results[0].address3,
    )
  }

  return (
    <>
      {!countries && (
        <div
          style={{
            display: 'flex',
            height: '272px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner />
        </div>
      )}

      {countries && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'stretch',
            alignItems: 'stretch',
          }}
        >
          <Form.Item
            name={['addresses_attributes', '0', 'label']}
            initialValue="住所"
            hidden
          />
          <Form.Item
            label={t('国')}
            name={['addresses_attributes', '0', 'country_number']}
            rules={[{ required: true, message: t('必須項目です') }]}
            initialValue={{ value: japan?.translations.en, key: japan?.number }}
          >
            <Select allowClear placeholder={t('選択してください')}>
              {countries.map((country: Country) => {
                return (
                  <Select.Option value={country.number} key={country.number}>
                    {country.translations.en}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>
          <Row gutter={24}>
            <Col xs={12}>
              <Form.Item
                label={t('郵便番号')}
                name={['addresses_attributes', '0', 'postal_code']}
                rules={[{ required: true, message: t('必須項目です') }]}
              >
                <Input placeholder={t('例) 1500013')} />
              </Form.Item>
            </Col>
            <Col
              xs={12}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                paddingBottom: '24px',
                textAlign: 'center',
              }}
            >
              <Button
                style={{
                  padding: xs ? '16px 10px' : '16px',
                  fontSize: '16px',
                  width: '100%',
                }}
                size="md"
                type="secondary"
                onClick={handleAddressFromPostalInit}
              >
                {t('住所を自動入力')}
              </Button>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('都道府県')}
                name={['addresses_attributes', '0', 'state']}
                rules={[{ required: true, message: t('必須項目です') }]}
              >
                <Input placeholder={t('例) 東京都')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('市区町村')}
                name={['addresses_attributes', '0', 'city']}
                rules={[{ required: true, message: t('必須項目です') }]}
              >
                <Input placeholder={t('例) 渋谷区')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('番地')}
                name={['addresses_attributes', '0', 'address1']}
                rules={[{ required: true, message: t('必須項目です') }]}
              >
                <Input placeholder={t('例) 恵比寿1-21-8')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('建物・ビル名')}
                name={['addresses_attributes', '0', 'address2']}
              >
                <Input placeholder={t('例) VORT恵比寿Ⅲ 2階')} />
              </Form.Item>
            </Col>
          </Row>
        </div>
      )}
    </>
  )
}
